import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatDate, inputCls, labelCls, btnPrimary } from "@/lib/constants";
import { Modal } from "@/components/Modal";
import { useAuth, useCanAdmin } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ShieldCheck, ShieldAlert, ChevronRight, UserCheck, UserCog, KeyRound, Copy, Eye, FileUp, Mail, FileSignature, Send, Download } from "lucide-react";

const KYC = { verified: "bg-emerald-100 text-emerald-700 border-emerald-300", pending: "bg-amber-50 text-amber-700 border-amber-200", rejected: "bg-rose-50 text-rose-700 border-rose-200" };
const PARTNER_DOC_TYPES = ["PAN", "Aadhaar", "GST", "Photo", "Bank Proof", "Agreement", "Other"];

const empty = { name: "", firm_name: "", email: "", phone: "", partner_type: "dsa", parent_id: "", pan: "", aadhaar: "", gst: "", bank_name: "", account_number: "", ifsc: "", account_holder: "", agreement_signed: false, agreement_date: "", kyc_status: "pending", status: "active", city: "", dob: "", gender: "", state: "", pincode: "", address: "", business_type: "Individual" };
const BUSINESS_TYPES = ["Individual", "Partnership Firm", "LLP", "Pvt Ltd"];

export default function Partners() {
  const { user } = useAuth();
  const canAdmin = useCanAdmin();
  const isAdmin = user?.role === "admin";
  const canManagePartners = canAdmin("partners");
  const canAdd = canManagePartners || user?.role === "sales_manager";
  const [partners, setPartners] = useState([]);
  const [types, setTypes] = useState([]);
  const [show, setShow] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [salesManagers, setSalesManagers] = useState([]);
  const [mgrModal, setMgrModal] = useState(null);
  const [smId, setSmId] = useState("");
  const [pwd, setPwd] = useState("");
  const [cred, setCred] = useState(null);
  const [approveDocs, setApproveDocs] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);
  // edit-modal documents
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("PAN");
  const [editPreview, setEditPreview] = useState(null);
  // new-partner staged documents (before partner is created)
  const [pendingDocs, setPendingDocs] = useState([]);
  const [esignInfo, setEsignInfo] = useState(null);
  const [pwdFor, setPwdFor] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [partnerRules, setPartnerRules] = useState({});
  const req = (k) => (partnerRules[k]?.required ? " *" : "");
  const isReq = (k) => !!partnerRules[k]?.required;

  const savePwd = async () => {
    try {
      await api.post(`/partners/${pwdFor.id}/set-password`, { password: newPwd });
      toast.success(`Password updated for ${pwdFor.name}`);
      setPwdFor(null); setNewPwd("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const load = useCallback(() => api.get("/partners").then((r) => setPartners(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const resendOnboarding = async (p) => {
    try {
      await api.post(`/partners/${p.id}/resend-onboarding`);
      toast.success(`Onboarding email re-sent to ${p.email}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const genAgreement = async (p) => {
    try {
      await api.post(`/partners/${p.id}/agreement`);
      toast.success("Agreement PDF generated & attached to documents");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const sendEsign = async (p) => {
    try {
      const { data } = await api.post(`/partners/${p.id}/agreement/send-esign`);
      if (data.email_sent) toast.success(`Agreement sent for e-sign — link emailed to ${p.email}`);
      else toast.success("Agreement sent for e-sign" + (p.email ? " (email delivery failed — share link manually)" : " — no partner email on file"));
      if (data.sign_url) window.open(data.sign_url, "_blank");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const resendEsignLink = async (p) => {
    try {
      const { data } = await api.post(`/partners/${p.id}/agreement/resend-esign-link`);
      toast.success(`Sign link re-emailed to ${data.email}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const syncSigned = async (p) => {
    try {
      await api.post(`/partners/${p.id}/agreement/sync-signed`);
      toast.success("Signed agreement synced & replaced");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  useEffect(() => {
    api.get("/sales-managers").then((r) => setSalesManagers(r.data)).catch(() => {});
    api.get("/partner-types").then((r) => setTypes(r.data)).catch(() => {});
    api.get("/public/field-rules").then((r) => setPartnerRules(r.data.rules?.partner || {})).catch(() => {});
  }, []);

  const typeLabel = (key) => types.find((t) => t.key === key)?.label || key;

  const openApprove = async (p) => {
    setMgrModal({ partner: p, mode: "approve" }); setSmId(p.sales_manager_id || ""); setPwd(""); setPreviewDoc(null); setApproveDocs([]);
    try { const { data } = await api.get("/documents", { params: { entity_type: "partner", entity_id: p.id } }); setApproveDocs(data); } catch (e) { console.error("Failed to load partner documents", e); }
  };
  const openReassign = (p) => { setMgrModal({ partner: p, mode: "reassign" }); setSmId(p.sales_manager_id || ""); };
  const viewDoc = async (id, setter) => {
    try { const { data } = await api.get(`/documents/${id}`); setter(data); } catch (e) { toast.error("Could not load document"); }
  };

  const submitManager = async (e) => {
    e.preventDefault();
    if (!smId) { toast.error("Please select a Sales Manager"); return; }
    const p = mgrModal.partner;
    try {
      if (mgrModal.mode === "approve") {
        const { data } = await api.post(`/partners/${p.id}/approve`, { sales_manager_id: smId, password: pwd || null });
        setCred(data); setMgrModal(null); load();
      } else {
        await api.patch(`/partners/${p.id}/assign-manager`, { sales_manager_id: smId });
        toast.success("Sales manager updated"); setMgrModal(null); load();
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const loadDocs = (pid) => api.get("/documents", { params: { entity_type: "partner", entity_id: pid } }).then((r) => setDocs(r.data)).catch(() => setDocs([]));
  const loadEsignEvents = (pid) => api.get(`/partners/${pid}/esign-events`).then((r) => setEsignInfo(r.data)).catch(() => setEsignInfo(null));
  const openNew = () => { setForm({ ...empty, partner_type: types[0]?.key || "dsa" }); setEditId(null); setDocs([]); setEditPreview(null); setPendingDocs([]); setDocType("PAN"); setEsignInfo(null); setShow(true); };
  const openEdit = (p) => { setForm({ ...empty, ...p, parent_id: p.parent_id || "" }); setEditId(p.id); setEditPreview(null); setDocType("PAN"); setPendingDocs([]); setEsignInfo(null); setShow(true); loadDocs(p.id); loadEsignEvents(p.id); };

  const uploadDoc = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      if (editId) {
        try {
          await api.post("/documents", { entity_type: "partner", entity_id: editId, doc_type: docType, file_name: file.name, data: reader.result });
          toast.success("Document uploaded"); loadDocs(editId);
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
      } else {
        setPendingDocs((arr) => [...arr, { doc_type: docType, file_name: file.name, data: reader.result }]);
        toast.success(`${file.name} staged — will upload after Save`);
      }
    };
    reader.readAsDataURL(file);
  };
  const delDoc = async (id) => { if (!window.confirm("Delete this document? This cannot be undone.")) return; await api.delete(`/documents/${id}`); toast.success("Document removed"); loadDocs(editId); if (editPreview?.id === id) setEditPreview(null); };
  const delPendingDoc = (idx) => { if (!window.confirm("Remove this document from upload queue?")) return; setPendingDocs((arr) => arr.filter((_, i) => i !== idx)); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, parent_id: form.parent_id || null };
      let pid = editId;
      if (editId) {
        await api.put(`/partners/${editId}`, payload);
      } else {
        const { data } = await api.post("/partners", payload);
        pid = data.id;
        for (const d of pendingDocs) {
          try { await api.post("/documents", { entity_type: "partner", entity_id: pid, doc_type: d.doc_type, file_name: d.file_name, data: d.data }); }
          catch (err) { toast.error(`Failed to upload ${d.file_name}`); }
        }
      }
      toast.success(editId ? "Partner updated" : `Partner added${pendingDocs.length ? ` with ${pendingDocs.length} document(s)` : ""}`);
      setPendingDocs([]);
      setShow(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this partner?")) return;
    await api.delete(`/partners/${id}`); toast.success("Partner deleted"); load();
  };

  const byType = (t) => partners.filter((p) => p.partner_type === t);
  const parentOptions = partners.filter((p) => p.id !== editId);

  const DocPreview = ({ doc, onClose }) => (
    <div className="mt-3 border border-slate-200 rounded-lg p-2 bg-white" data-testid="doc-preview">
      <div className="flex items-center justify-between mb-2"><span className="text-xs font-medium text-slate-600">{doc.doc_type} · {doc.file_name}</span><button type="button" onClick={onClose} className="text-xs text-slate-400 hover:text-rose-600">Close</button></div>
      {(doc.data || "").startsWith("data:image")
        ? <img src={doc.data} alt={doc.file_name} className="max-h-64 rounded mx-auto" />
        : (doc.data || "").startsWith("data:application/pdf")
          ? <iframe title="doc-preview" src={doc.data} className="w-full h-64 rounded" />
          : <a href={doc.data} download={doc.file_name} className="text-sm text-emerald-600">Download {doc.file_name}</a>}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight">Partners</h1>
          <p className="text-sm text-slate-500 mt-1">Manage DSA hierarchy, KYC and agreements</p>
        </div>
        <button data-testid="add-partner-button" onClick={openNew} className={`${btnPrimary} ${canAdd ? "" : "hidden"}`}><Plus size={16} /> Add Partner</button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {types.map((t) => (
          <div key={t.key} className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider font-semibold">
              <ChevronRight size={14} /> {t.label}
            </div>
            <div className="text-2xl font-extrabold text-slate-900 font-heading mt-1">{byType(t.key).length}</div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left px-4 py-3">Partner</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Reports To</th>
                <th className="text-left px-4 py-3">PAN / GST</th>
                <th className="text-left px-4 py-3">KYC</th>
                <th className="text-left px-4 py-3">Sales Manager</th>
                <th className="text-left px-4 py-3">Login</th>
                <th className="text-left px-4 py-3">Agreement</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((p) => {
                const parent = partners.find((x) => x.id === p.parent_id);
                return (
                  <tr key={p.id} data-testid={`partner-row-${p.id}`} className="border-b border-slate-100 hover:bg-slate-50/80 text-sm text-slate-700">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{p.name}</div>
                      {p.firm_name && <div className="text-xs text-emerald-600 font-medium">{p.firm_name}{p.business_type ? ` · ${p.business_type}` : ""}</div>}
                      <div className="text-xs text-slate-400">{p.phone} · {p.city}</div>
                    </td>
                    <td className="px-4 py-3">{typeLabel(p.partner_type)}</td>
                    <td className="px-4 py-3 text-slate-500">{parent?.name || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{p.pan || "—"}<br />{p.gst || ""}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${KYC[p.kyc_status]}`}>
                        {p.kyc_status === "verified" ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />} {p.kyc_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{p.sales_manager_name || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3">
                      {p.approved
                        ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium"><KeyRound size={12} /> Active</span>
                        : <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium">Pending</span>}
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const s = (p.esign_status || "").toLowerCase();
                        const map = {
                          "signed": ["bg-emerald-100 text-emerald-700 border-emerald-300", "Signed"],
                          "sent": ["bg-blue-50 text-blue-700 border-blue-200", "Sent"],
                          "rejected": ["bg-rose-50 text-rose-700 border-rose-200", "Rejected"],
                          "expired": ["bg-slate-100 text-slate-600 border-slate-200", "Expired"],
                          "sign_completed_fetch_failed": ["bg-amber-50 text-amber-700 border-amber-200", "Signed · fetch failed"],
                        };
                        const [cls, label] = map[s] || (p.agreement_signed ? ["bg-emerald-50 text-emerald-700 border-emerald-200", "Signed (offline)"] : ["bg-slate-50 text-slate-400 border-slate-200", "—"]);
                        return (
                          <div className="flex items-center gap-1.5">
                            <span data-testid={`agreement-status-${p.id}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>{label}</span>
                            {(s === "signed" || s === "sign_completed_fetch_failed") && canManagePartners && (
                              <button data-testid={`fetch-signed-doc-${p.id}`} title="Fetch signed PDF from Leegality" onClick={() => syncSigned(p)} className="p-1 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-50"><Download size={13} /></button>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {canManagePartners ? (
                          <>
                            {!p.approved && <button data-testid={`approve-partner-${p.id}`} title="Approve & create login" onClick={() => openApprove(p)} className="p-2 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-50"><UserCheck size={15} /></button>}
                            {p.approved && <button data-testid={`reassign-manager-${p.id}`} title="Change sales manager" onClick={() => openReassign(p)} className="p-2 rounded-lg text-slate-500 hover:text-purple-600 hover:bg-purple-50"><UserCog size={15} /></button>}
                            {p.approved && p.email && <button data-testid={`resend-onboarding-${p.id}`} title="Resend onboarding email" onClick={() => resendOnboarding(p)} className="p-2 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50"><Mail size={15} /></button>}
                            {p.approved && <button data-testid={`set-partner-password-${p.id}`} title="Set login password" onClick={() => { setPwdFor(p); setNewPwd(""); }} className="p-2 rounded-lg text-slate-500 hover:text-amber-600 hover:bg-amber-50"><KeyRound size={15} /></button>}
                            <button data-testid={`edit-partner-${p.id}`} onClick={() => openEdit(p)} className="p-2 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-50"><Pencil size={15} /></button>
                            {(() => {
                              const signed = p.esign_status === "signed";
                              const disabledCls = signed ? "opacity-40 cursor-not-allowed" : "";
                              return (
                                <>
                                  <button disabled={signed} data-testid={`gen-agreement-${p.id}`} title={signed ? "Already signed — cannot regenerate" : "Generate agreement PDF"} onClick={() => !signed && genAgreement(p)} className={`p-2 rounded-lg text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 ${disabledCls}`}><FileSignature size={15} /></button>
                                  <button disabled={signed} data-testid={`send-esign-${p.id}`} title={signed ? "Already signed — cannot resend" : "Send for e-sign"} onClick={() => !signed && sendEsign(p)} className={`p-2 rounded-lg text-slate-500 hover:text-purple-600 hover:bg-purple-50 ${disabledCls}`}><Mail size={15} /></button>
                                  {p.esign_status === "sent" && p.esign_url && <button data-testid={`resend-esign-link-${p.id}`} title="Resend sign link" onClick={() => resendEsignLink(p)} className="p-2 rounded-lg text-slate-500 hover:text-teal-600 hover:bg-teal-50"><Send size={15} /></button>}
                                </>
                              );
                            })()}
                            {p.esign_status === "sent" && <button data-testid={`sync-signed-${p.id}`} title="Sync signed agreement" onClick={() => syncSigned(p)} className="p-2 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-50"><ShieldCheck size={15} /></button>}
                            <button data-testid={`delete-partner-${p.id}`} onClick={() => del(p.id)} className="p-2 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={15} /></button>
                          </>
                        ) : <span className="text-xs text-slate-300">—</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {partners.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400 text-sm">No partners yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={show} onClose={() => setShow(false)} title={editId ? "Edit Partner" : "Add Partner"} size="lg" testId="partner-modal">
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>Name *</label><input data-testid="partner-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Firm Name{req("firm_name")}</label><input data-testid="partner-firm-name-input" required={isReq("firm_name")} value={form.firm_name} onChange={(e) => setForm({ ...form, firm_name: e.target.value })} className={inputCls} placeholder="Registered firm / company name" /></div>
          <div><label className={labelCls}>Partner Type *</label>
            <select data-testid="partner-type-select" value={form.partner_type} onChange={(e) => setForm({ ...form, partner_type: e.target.value })} className={inputCls}>
              {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Email{req("email")}</label><input required={isReq("email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Phone{req("phone")}</label><input required={isReq("phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Business Type</label>
            <select data-testid="partner-business-type-select" value={form.business_type} onChange={(e) => setForm({ ...form, business_type: e.target.value })} className={inputCls}>
              {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Reports To</label>
            <select data-testid="partner-parent-select" value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} className={inputCls}>
              <option value="">None (top level)</option>
              {parentOptions.map((p) => <option key={p.id} value={p.id}>{p.name} ({typeLabel(p.partner_type)})</option>)}
            </select>
          </div>
          <div><label className={labelCls}>DOB</label><input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Gender</label>
            <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className={inputCls}>
              <option value="">Select…</option><option value="Male">Male</option><option value="Female">Female</option><option value="Other">Other</option>
            </select>
          </div>
          <div className="sm:col-span-2"><label className={labelCls}>Address{req("address")}</label><input required={isReq("address")} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>City{req("city")}</label><input required={isReq("city")} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>State{req("state")}</label><input required={isReq("state")} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Pincode{req("pincode")}</label><input required={isReq("pincode")} value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} className={inputCls} /></div>

          <div className="sm:col-span-2 border-t border-slate-100 pt-3 text-xs font-semibold uppercase tracking-wider text-slate-400">KYC</div>
          <div><label className={labelCls}>PAN{req("pan")}</label><input required={isReq("pan")} value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Aadhaar{req("aadhaar")}</label><input required={isReq("aadhaar")} value={form.aadhaar} onChange={(e) => setForm({ ...form, aadhaar: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>GST{req("gst")}</label><input required={isReq("gst")} value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>KYC Status</label>
            <select value={form.kyc_status} onChange={(e) => setForm({ ...form, kyc_status: e.target.value })} className={inputCls}>
              <option value="pending">Pending</option><option value="verified">Verified</option><option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="sm:col-span-2 border-t border-slate-100 pt-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Bank Details</div>
          <div><label className={labelCls}>Bank Name</label><input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Account Number</label><input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>IFSC</label><input value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Account Holder</label><input value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} className={inputCls} /></div>
          <div className="flex items-center gap-2 pt-6">
            <input data-testid="agreement-checkbox" type="checkbox" checked={form.agreement_signed} onChange={(e) => setForm({ ...form, agreement_signed: e.target.checked })} className="w-4 h-4 accent-emerald-600" />
            <label className="text-sm text-slate-700">Agreement Signed</label>
          </div>
          <div><label className={labelCls}>Agreement Date</label><input type="date" value={form.agreement_date} onChange={(e) => setForm({ ...form, agreement_date: e.target.value })} className={inputCls} /></div>

          {editId && (esignInfo?.document_id || esignInfo?.events?.length > 0) && (
            <div className="sm:col-span-2 border-t border-slate-100 pt-3 space-y-3" data-testid="esign-timeline-section">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">e-Sign · Leegality</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Leegality Doc ID</div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <div data-testid="leegality-doc-id" className="text-xs font-mono font-semibold text-slate-800 break-all">{esignInfo?.document_id || "—"}</div>
                    {esignInfo?.document_id && <button type="button" onClick={() => { navigator.clipboard?.writeText(esignInfo.document_id); toast.success("Doc ID copied"); }} className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 flex-shrink-0"><Copy size={13} /></button>}
                  </div>
                </div>
                <div className="px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Current Status</div>
                  <div className="mt-0.5"><span className="px-2 py-0.5 rounded-full text-xs font-medium border capitalize bg-emerald-50 text-emerald-700 border-emerald-300">{esignInfo?.esign_status || "—"}</span></div>
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Signing Timeline ({esignInfo?.events?.length || 0})</div>
                {(!esignInfo?.events || esignInfo.events.length === 0) && <p className="text-xs text-slate-400">No webhook events received yet. Timeline will populate once Leegality posts to the webhook.</p>}
                <ol className="relative border-l-2 border-emerald-200 ml-2 space-y-3 pl-4">
                  {(esignInfo?.events || []).map((ev, i) => {
                    const act = (ev.action || ev.document_status || ev.webhook_type || "event").toLowerCase();
                    const color = act.includes("sign") ? "bg-emerald-500" : (act.includes("reject") || act.includes("expired")) ? "bg-rose-500" : act.includes("view") ? "bg-blue-500" : "bg-slate-400";
                    return (
                      <li key={i} className="relative" data-testid={`esign-event-${i}`}>
                        <span className={`absolute -left-[22px] top-1 w-3 h-3 rounded-full ring-2 ring-white ${color}`} />
                        <div className="text-sm font-semibold text-slate-800 capitalize">{ev.action || ev.document_status || ev.webhook_type || "Event"}</div>
                        <div className="text-[11px] text-slate-500">
                          {new Date(ev.received_at).toLocaleString()}
                          {ev.invitee_name ? ` · ${ev.invitee_name}` : ""}
                          {ev.invitee_email ? ` (${ev.invitee_email})` : ""}
                        </div>
                        {(ev.document_status || ev.webhook_type) && (
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-mono mt-0.5">
                            {ev.webhook_type && <span className="mr-2">type: {ev.webhook_type}</span>}
                            {ev.document_status && <span>status: {ev.document_status}</span>}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>
            </div>
          )}

          {editId ? (
            <div className="sm:col-span-2 border-t border-slate-100 pt-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Documents ({docs.length})</div>
              <div className="flex gap-3 items-end flex-wrap mb-3">
                <div className="flex-1 min-w-[160px]">
                  <label className={labelCls}>Document Type</label>
                  <select data-testid="partner-doc-type-select" value={docType} onChange={(e) => setDocType(e.target.value)} className={inputCls}>
                    {PARTNER_DOC_TYPES.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <label className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium text-sm border border-slate-200 flex items-center gap-2 cursor-pointer" data-testid="partner-doc-upload-label">
                  <FileUp size={16} /> Upload
                  <input data-testid="partner-doc-upload-input" type="file" className="hidden" onChange={uploadDoc} />
                </label>
              </div>
              <div className="space-y-2">
                {docs.length === 0 && <p className="text-xs text-slate-400">No documents uploaded.</p>}
                {docs.map((d) => (
                  <div key={d.id} className="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-200 bg-slate-50">
                    <div><div className="text-sm font-medium text-slate-800">{d.doc_type}</div><div className="text-xs text-slate-400">{d.file_name}</div></div>
                    <div className="flex gap-1">
                      <button type="button" data-testid={`view-partner-doc-${d.id}`} onClick={() => viewDoc(d.id, setEditPreview)} className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"><Eye size={15} /></button>
                      <button type="button" data-testid={`delete-partner-doc-${d.id}`} onClick={() => delDoc(d.id)} className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
              {editPreview && <DocPreview doc={editPreview} onClose={() => setEditPreview(null)} />}
            </div>
          ) : (
            <div className="sm:col-span-2 border-t border-slate-100 pt-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Documents ({pendingDocs.length})</div>
              <div className="flex gap-3 items-end flex-wrap mb-3">
                <div className="flex-1 min-w-[160px]">
                  <label className={labelCls}>Document Type</label>
                  <select data-testid="partner-doc-type-select-new" value={docType} onChange={(e) => setDocType(e.target.value)} className={inputCls}>
                    {PARTNER_DOC_TYPES.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <label className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium text-sm border border-slate-200 flex items-center gap-2 cursor-pointer" data-testid="partner-doc-upload-label-new">
                  <FileUp size={16} /> Attach
                  <input data-testid="partner-doc-upload-input-new" type="file" className="hidden" onChange={uploadDoc} />
                </label>
              </div>
              <div className="space-y-2">
                {pendingDocs.length === 0 && <p className="text-xs text-slate-400">Attach KYC / bank / agreement documents. They will be uploaded after you click Add Partner.</p>}
                {pendingDocs.map((d, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-200 bg-slate-50">
                    <div><div className="text-sm font-medium text-slate-800">{d.doc_type}</div><div className="text-xs text-slate-400">{d.file_name}</div></div>
                    <button type="button" data-testid={`delete-pending-doc-${i}`} onClick={() => delPendingDoc(i)} className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setShow(false)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
            <button data-testid="submit-partner-button" type="submit" className={btnPrimary}>{editId ? "Save Changes" : "Add Partner"}</button>
          </div>
        </form>
      </Modal>

      <Modal open={!!mgrModal} onClose={() => setMgrModal(null)} title={mgrModal?.mode === "approve" ? "Approve Partner & Create Login" : "Change Sales Manager"} testId="approve-modal">
        <form onSubmit={submitManager} className="space-y-4">
          {mgrModal?.mode === "approve" && (
            <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              Approving <span className="font-semibold text-slate-800">{mgrModal?.partner?.name}</span> will verify their KYC and create a partner login for <span className="font-mono text-slate-800">{mgrModal?.partner?.email || "(no email!)"}</span>.
            </div>
          )}
          <div>
            <label className={labelCls}>Map to Sales Manager *</label>
            <select data-testid="approve-manager-select" value={smId} onChange={(e) => setSmId(e.target.value)} className={inputCls}>
              <option value="">Select sales manager…</option>
              {salesManagers.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.email})</option>)}
            </select>
            {salesManagers.length === 0 && <p className="text-xs text-amber-600 mt-1">No sales managers yet. Add one from the Team page first.</p>}
          </div>
          {mgrModal?.mode === "approve" && (
            <div>
              <label className={labelCls}>Password (leave blank to auto-generate)</label>
              <input data-testid="approve-password-input" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} className={inputCls} placeholder="Auto-generated if empty" />
              <p className="text-xs text-slate-400 mt-1">An onboarding email with a secure set-password link is always sent to the partner.</p>
            </div>
          )}
          {mgrModal?.mode === "approve" && (
            <div>
              <label className={labelCls}>KYC Documents ({approveDocs.length})</label>
              {approveDocs.length === 0 && <p className="text-xs text-slate-400">No documents uploaded by this partner.</p>}
              <div className="space-y-2">
                {approveDocs.map((d) => (
                  <div key={d.id} className="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-200 bg-slate-50">
                    <div><div className="text-sm font-medium text-slate-800">{d.doc_type}</div><div className="text-xs text-slate-400">{d.file_name}</div></div>
                    <button type="button" data-testid={`view-doc-${d.id}`} onClick={() => viewDoc(d.id, setPreviewDoc)} className="text-xs px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-emerald-400 text-slate-700 flex items-center gap-1"><Eye size={13} /> View</button>
                  </div>
                ))}
              </div>
              {previewDoc && <DocPreview doc={previewDoc} onClose={() => setPreviewDoc(null)} />}
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setMgrModal(null)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
            <button data-testid="approve-submit-button" type="submit" className={btnPrimary}>{mgrModal?.mode === "approve" ? "Approve & Create Login" : "Update Manager"}</button>
          </div>
        </form>
      </Modal>

      <Modal open={!!cred} onClose={() => setCred(null)} title="Partner Approved" testId="credentials-modal">
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium">
            <ShieldCheck size={18} /> Partner approved and login created.
          </div>
          <div className={`text-sm rounded-lg px-3 py-2 border ${cred?.email_sent ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}>
            {cred?.email_sent
              ? "An onboarding email with a secure set-password link has been sent to the partner."
              : "Approved, but the onboarding email could not be sent. The partner can use 'Forgot password?' to set their password."}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50">
              <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Login Email</div><div className="text-sm font-mono font-semibold text-slate-800">{cred?.email}</div></div>
              <button onClick={() => { navigator.clipboard?.writeText(cred?.email || ""); toast.success("Email copied"); }} className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"><Copy size={15} /></button>
            </div>
            <div className="px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50"><div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Sales Manager</div><div className="text-sm font-semibold text-slate-800">{cred?.sales_manager}</div></div>
            {cred?.password && (
              <div className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50">
                <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Admin-set Password</div><div className="text-sm font-mono font-semibold text-slate-800">{cred.password}</div></div>
                <button onClick={() => { navigator.clipboard?.writeText(cred?.password || ""); toast.success("Password copied"); }} className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"><Copy size={15} /></button>
              </div>
            )}
          </div>
          {!cred?.password && <p className="text-xs text-slate-500">The partner will set their own password via the emailed link. No password is shown here for security.</p>}
          <div className="flex justify-end"><button data-testid="credentials-done-button" onClick={() => setCred(null)} className={btnPrimary}>Done</button></div>
        </div>
      </Modal>
      <Modal open={!!pwdFor} onClose={() => setPwdFor(null)} title="Set Partner Password" testId="set-partner-password-modal">
        <div className="space-y-4">
          <div className="text-sm text-slate-600">Set a new login password for <span className="font-semibold text-slate-800">{pwdFor?.name}</span> ({pwdFor?.email}). This replaces their current password immediately.</div>
          <div>
            <label className={labelCls}>New Password *</label>
            <input data-testid="new-partner-password-input" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className={inputCls} placeholder="Min 6 characters" />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setPwdFor(null)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
            <button data-testid="confirm-set-partner-password-button" onClick={savePwd} disabled={newPwd.length < 6} className={`${btnPrimary} disabled:opacity-50`}>Update Password</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
